import React from "react";
import { motion } from "framer-motion";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_we-gotta-gift/artifacts/i9vj0gj4_DSC09023%20%281%29.jpg";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative bg-cream pt-24 pb-14 lg:pt-32 lg:pb-20"
    >
      <div className="max-w-[1400px] mx-auto container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* left: copy */}
          <div className="lg:col-span-7">
            <motion.h1
              data-testid="hero-headline"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-bold text-[clamp(2.5rem,7vw,6rem)] leading-[1.02] tracking-[-0.025em] text-ink"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 60, "WONK" 0' }}
            >
              True industry
              <br />
              icons rarely
              <br />
              share their
              <br />
              blueprints.{" "}
              <span
                className="italic text-gold-bright"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1' }}
              >
                Until now.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-10 max-w-xl text-base lg:text-[17px] leading-[1.7] text-body"
            >
              We Gotta Gift is curating the definitive chronicle of industry
              excellence. Thirty legendary suppliers and distributors. One
              masterfully told story of how they rewrote the rules and built
              their empires. Captured in a premium hardcover. Broadcast to a
              global audience. Etched in history.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-12 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-nominate"
                onClick={() => scrollTo("apply")}
                className="group inline-flex items-center gap-2 bg-ink text-cream px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] hover:bg-burgundy transition-colors duration-300"
              >
                Nominate your company
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
              <button
                data-testid="hero-cta-book"
                onClick={() => scrollTo("the-book")}
                className="inline-flex items-center gap-2 bg-transparent text-ink border border-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] hover:bg-ink hover:text-cream transition-all duration-300"
              >
                See the book
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] tracking-[0.18em] text-burgundy uppercase"
              data-testid="hero-trust-strip"
            >
              <span className="text-muted-ink">Trusted by</span>
              <span className="h-px w-6 bg-ink/20" />
              <span>PPAI 100 leaders</span>
              <span className="text-muted-ink/60">&middot;</span>
              <span>IGC Global</span>
              <span className="text-muted-ink/60">&middot;</span>
              <span>Consortium Gifts</span>
            </motion.div>
          </div>

          {/* right: author portrait card */}
          <div className="lg:col-span-5">
            <motion.figure
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              data-testid="author-card"
              className="relative bg-cream-2 border border-line aspect-[4/5] w-full max-w-md mx-auto lg:ml-auto overflow-hidden"
            >
              <img
                src={PORTRAIT}
                alt="Gaurav Bhagat — Founder, We Gotta Gift; President, IGC Global Promotions"
                className="absolute inset-0 w-full h-full object-cover object-[50%_25%]"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />
              <figcaption className="absolute left-3 right-3 bottom-3 bg-ink text-cream font-mono text-[10px] tracking-[0.05em] px-3 py-2.5">
                Gaurav Bhagat &mdash; Founder, We Gotta Gift &middot; President, IGC Global Promotions
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  );
}
